import { Box, Flex, Menu, Text } from "@chakra-ui/react"

export interface SingleSelectOption {
  value: string
  label: string
}

interface SingleSelectMenuProps {
  options: SingleSelectOption[]
  value: string
  onChange: (value: string) => void
  /** Accessible name for the trigger. */
  ariaLabel?: string
}

/**
 * Pick-one counterpart of the multi-select asset menus used by the monitoring
 * charts (same trigger and row styling). Renders nothing for fewer than two
 * options, since there is nothing to choose between.
 */
const SingleSelectMenu = ({ options, value, onChange, ariaLabel = "Select asset" }: SingleSelectMenuProps) => {
  if (options.length < 2) return null
  const selected = options.find((o) => o.value === value) ?? options[0]

  return (
    <Menu.Root
      closeOnSelect
      onSelect={(details) => onChange(details.value)}
    >
      <Menu.Trigger asChild>
        <Box
          as="button"
          aria-label={ariaLabel}
          padding={2}
          borderWidth="1px"
          borderRadius="md"
          borderColor="border"
          backgroundColor="white"
          cursor="pointer"
          width="100%"
          maxW={{ base: "100%", md: "560px" }}
          textAlign="left"
          fontSize="sm"
          _hover={{ borderColor: "blue.300" }}
        >
          <Flex alignItems="center" justifyContent="space-between">
            <Text truncate>{selected.label}</Text>
            <Text fontSize="xs" color="gray.500">
              ▼
            </Text>
          </Flex>
        </Box>
      </Menu.Trigger>
      <Menu.Positioner>
        <Menu.Content width="400px" maxH="500px" overflowY="auto">
          {options.map((option) => {
            const isSelected = option.value === selected.value
            return (
              <Box key={option.value} mb={1} mx={2}>
                <Menu.Item
                  value={option.value}
                  cursor="pointer"
                  _hover={{ backgroundColor: "gray.50" }}
                  borderRadius="sm"
                >
                  <Flex alignItems="center" gap={2}>
                    <input type="radio" checked={isSelected} readOnly tabIndex={-1} aria-hidden />
                    <Text fontSize="sm" fontWeight={isSelected ? "semibold" : "normal"}>
                      {option.label}
                    </Text>
                  </Flex>
                </Menu.Item>
              </Box>
            )
          })}
        </Menu.Content>
      </Menu.Positioner>
    </Menu.Root>
  )
}

export default SingleSelectMenu
